import { Controller } from 'stimulus';
import { initTemplatePicker } from '../lib/template_picker';
const TomSelect = require('../lib/tom_select');

// Floating popup editor used by the list view for any multi-input edit
// (rail date pairs, custom job column values, …). One shared popup element
// appended to <body> and reused across openers.
//
// Each opener (open / openColumn / …) configures an `activeEditor` descriptor
// that tells `save()` where to POST/PATCH, how to build the body, and which
// DOM target to refresh with the response. That lets dates and columns share
// every other piece of the popup lifecycle (open/close/position/escape/etc.).
//
// Because the popup lives outside this.element (so it can float freely with
// position: fixed without clipping), Stimulus 1.x doesn't auto-wire its
// data-action / data-target attributes. We bind events manually after each
// innerHTML injection — mirroring the automated_schedule controller.
export default class extends Controller {
  connect() {
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundMousedown = this.handleMousedown.bind(this);
    this.boundKeydown = this.handleGlobalKey.bind(this);
    document.addEventListener('click', this.boundOutsideClick, true);
    document.addEventListener('mousedown', this.boundMousedown, true);
    document.addEventListener('keydown', this.boundKeydown);
    this.popupElement = document.createElement('div');
    this.popupElement.className = 'hidden fixed bg-white border rounded-lg shadow-lg';
    this.popupElement.style.zIndex = '9999';
    this.popupElement.dataset.popupEditor = 'jobs';
    document.body.appendChild(this.popupElement);
    this.currentTrigger = null;
    this.activeEditor = null;
  }

  disconnect() {
    document.removeEventListener('click', this.boundOutsideClick, true);
    document.removeEventListener('mousedown', this.boundMousedown, true);
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.popupElement && this.popupElement.parentNode) {
      this.popupElement.parentNode.removeChild(this.popupElement);
    }
  }

  // ─── Openers ───────────────────────────────────────────────────────────────

  open(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const jobId = trigger.dataset.jobId;
    const pair = trigger.dataset.pair;
    if (!jobId || !pair) return;

    this.beginEdit(trigger, {
      formUrl: `/jobs/${jobId}/date_pair_form?pair=${encodeURIComponent(pair)}`,
      saveUrl: `/jobs/${jobId}/date_pair`,
      saveMethod: 'PATCH',
      refreshTargetId: `job-${jobId}-pair-${pair}`,
      prepareFormData: (popup) => {
        const fd = new FormData();
        fd.append('pair', pair);
        const est = popup.querySelector('input[name="estimated"]');
        const act = popup.querySelector('input[name="actual"]');
        if (est) fd.append('estimated', est.value);
        if (act) fd.append('actual', act.value);
        return fd;
      }
      // No onSaved hook needed — the swapped contents carry their own
      // state modifiers (jlv-df-inner--actual, jlv-df-inner--late, etc.),
      // so the wrapper class stays stable.
    });
  }

  openColumn(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const jobId = trigger.dataset.jobId;
    const columnId = trigger.dataset.jobColumnId;
    if (!jobId || !columnId) return;

    const query = new URLSearchParams({ job_column_id: columnId });

    this.beginEdit(trigger, {
      formUrl: `/jobs/${jobId}/job_column_values/popup_form?${query}`,
      saveUrl: `/jobs/${jobId}/job_column_values/popup_save?${query}`,
      saveMethod: 'PATCH',
      refreshTargetId: `job-${jobId}-column-${columnId}`,
      prepareFormData: (popup) => {
        const fd = new FormData();
        popup.querySelectorAll('[name^="job_column_value["]').forEach((f) => {
          fd.append(f.name, f.value);
        });
        return fd;
      }
    });
  }

  // Generic opener for fields on the Job model (status, people, location,
  // customer). Trigger carries data-job-id and data-field.
  openJobField(event) {
    // Let anchors nested inside a trigger (e.g. the template chip's
    // open-in-new-tab icon) navigate instead of opening the editor.
    if (event.target.closest && event.target.closest('a')) return;
    event.preventDefault();
    const trigger = event.currentTarget;
    const jobId = trigger.dataset.jobId;
    const field = trigger.dataset.field;
    if (!jobId || !field) return;

    const query = new URLSearchParams({ field });
    // Density of the card the click came from, so the saved status re-renders as
    // the matching pill (compact .tlv-rstat vs expanded .jlv-pill).
    if (trigger.dataset.variant) query.set('variant', trigger.dataset.variant);

    this.beginEdit(trigger, {
      formUrl: `/jobs/${jobId}/popup_field_form?${query}`,
      saveUrl: `/jobs/${jobId}/popup_field_save?${query}`,
      saveMethod: 'PATCH',
      refreshTargetId: `job-${jobId}-field-${field}`,
      prepareFormData: (popup) => {
        const fd = new FormData();
        popup.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
          if (el.tagName === 'SELECT' && el.multiple) {
            Array.from(el.selectedOptions).forEach((opt) => fd.append(el.name, opt.value));
          } else if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) fd.append(el.name, el.value);
          } else {
            fd.append(el.name, el.value);
          }
        });
        return fd;
      },
      // A status change can flip the job's status-driven nav notices (the
      // "print the traveler" reminder and the "not submitted yet" draft note),
      // so refresh them in place rather than waiting for the next page load.
      onSaved: (target) => {
        if (field === 'status') this.refreshStatusNotices(jobId);
        // The template chip's empty/filled styling lives on the outer trigger,
        // outside the swapped fragment — sync it (no-op for the table cell).
        if (field === 'template') {
          const chip = trigger.closest('.jlv-sysp');
          if (chip) chip.classList.toggle('jlv-sysp--empty', !target.querySelector('.template-open-link'));
        }
      }
    });
  }

  // Re-fetch and swap the job-nav status notices fragment. No-op when the
  // container isn't on the page (e.g. the list view), and non-fatal on failure
  // (the notices still appear on the next full page load).
  refreshStatusNotices(jobId) {
    const container = document.getElementById(`job-${jobId}-status-notices`);
    if (!container) return;

    fetch(`/jobs/${jobId}/status_notices`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => { container.innerHTML = html; })
      .catch(() => { /* non-critical */ });
  }

  // ─── Shared lifecycle ──────────────────────────────────────────────────────

  beginEdit(trigger, editorDescriptor) {
    this.close();
    this.currentTrigger = trigger;
    this.activeEditor = editorDescriptor;

    fetch(editorDescriptor.formUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async (r) => {
        // Only a popup fragment may be injected. A redirect (sign-in page, or
        // a permission bounce from a server that still redirects) means the
        // body is a whole page, and an error status means it is a message —
        // show a notice rather than the page inside the popup.
        if (r.redirected || !r.ok) {
          this.showNotice(trigger, await this.blockedMessage(r));
          return;
        }
        const html = await r.text();
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        // Render off-screen-visible so we can measure real dimensions before
        // committing to a position (lets us flip above the trigger when there
        // isn't enough room below).
        this.popupElement.style.visibility = 'hidden';
        this.popupElement.classList.remove('hidden');
        this.position(trigger);
        this.popupElement.style.visibility = '';
        // The template picker's own input, when present, so the list opens
        // at once (the original <select> is hidden behind it); otherwise the
        // first field.
        const first = this.popupElement.querySelector('.ts-wrapper.tp .ts-control input')
          || this.popupElement.querySelector('input, select, textarea');
        if (first) first.focus();
      });
  }

  close() {
    if (!this.popupElement) return;
    if (this.tomSelectInstances) {
      this.tomSelectInstances.forEach((ts) => { try { ts.destroy(); } catch (_) { /* noop */ } });
      this.tomSelectInstances = [];
    }
    this.popupElement.classList.add('hidden');
    this.popupElement.innerHTML = '';
    const t = this.currentTrigger;
    this.currentTrigger = null;
    this.activeEditor = null;
    if (t) t.focus();
  }

  // Wire up Cancel / Save / per-input keydown after each fresh innerHTML inject.
  bindPopupEvents() {
    const cancelBtn = this.popupElement.querySelector('[data-popup-action="cancel"]');
    if (cancelBtn) cancelBtn.addEventListener('click', (e) => { e.preventDefault(); this.close(); });

    const saveBtn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (saveBtn) saveBtn.addEventListener('click', (e) => { e.preventDefault(); this.save(); });

    this.popupElement.querySelectorAll('input, select, textarea').forEach((input) => {
      input.addEventListener('keydown', (e) => this.handleKey(e));
    });

    // Init TomSelect on any multi-select. We track instances so we can destroy
    // them on close (otherwise the leftover DOM lingers as zombie nodes).
    this.tomSelectInstances = [];
    this.popupElement.querySelectorAll('[data-popup-multi-select]').forEach((el) => {
      const ts = new TomSelect(el, {
        plugins: ['remove_button'],
        persist: false,
        maxItems: null,
        hideSelected: true,
        create: el.dataset.popupAllowCreate === 'true',
        onItemAdd: function () {
          this.setTextboxValue('');
          this.refreshOptions(false);
        }
      });
      this.tomSelectInstances.push(ts);
    });

    // The template popup uses the shared template picker (recently used
    // first, every template by typing). Same destroy-on-close handling.
    this.popupElement.querySelectorAll('[data-template-picker]').forEach((el) => {
      this.tomSelectInstances.push(initTemplatePicker(el));
    });
  }

  save() {
    if (!this.activeEditor) return;
    const { saveUrl, saveMethod, refreshTargetId, prepareFormData, onSaved } = this.activeEditor;
    const body = prepareFormData(this.popupElement);

    this.clearErrors();
    this.showSpinner(true);

    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrf = csrfMeta ? csrfMeta.content : '';

    fetch(saveUrl, {
      method: saveMethod,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrf, Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body
    })
      .then(async (response) => {
        if (response.ok && !response.redirected) {
          const html = await response.text();
          const target = document.getElementById(refreshTargetId);
          if (target) {
            target.innerHTML = html;
            this.syncStatusVisuals(target);
            if (onSaved) onSaved(target, html);
          }
          this.close();
          return;
        }
        // Validation error — surface inline. Try JSON { errors: [...] } first,
        // fall back to the response body as a single message.
        let messages = [];
        const contentType = response.headers.get('content-type') || '';
        if (response.redirected) {
          messages = ['This change could not be saved. Reload the page and try again.'];
        } else if (contentType.includes('text/plain')) {
          const text = (await response.text()).trim();
          messages = [text || `Save failed (${response.status})`];
        } else if (contentType.includes('application/json')) {
          try {
            const json = await response.json();
            messages = Array.isArray(json.errors) ? json.errors : [String(json.error || response.statusText)];
          } catch (_) {
            messages = [`Save failed (${response.status})`];
          }
        } else {
          messages = [`Save failed (${response.status})`];
        }
        this.showErrors(messages);
        this.showSpinner(false);
      })
      .catch((err) => {
        console.error('Popup save failed:', err);
        this.showErrors(['Could not reach the server. Check your connection and try again.']);
        this.showSpinner(false);
      });
  }

  // Status AND date edits shift card-level bits beyond the swapped fragment: the
  // left stripe color (status only), the temporal-state class on the card root,
  // and the timing line ("Due in 5 days" → "Completed"). The swapped-in fragment
  // (status pill or date tile) carries the relevant pieces as data-* attrs; apply
  // them to the same card/row so it reflects the change without a reload. No-op
  // for other fields, whose fragments don't carry these attributes.
  syncStatusVisuals(target) {
    // Keyed off the timing attr so it fires for BOTH status edits (pill carries
    // stripe + state + timing) and date edits (date tile carries state + timing).
    const source = target.querySelector('[data-timing-phrase]');
    if (!source) return;
    const card = target.closest('.jlv-card, .jlv-crow');
    if (!card) return;
    const d = source.dataset;

    // Left stripe (.jlv-stripe expanded · .tlv-rstripe compact).
    const stripe = card.querySelector('.jlv-stripe, .tlv-rstripe');
    if (stripe && d.stripeColor) stripe.style.background = d.stripeColor;

    // Temporal-state class on the card root.
    if (d.cardState) {
      card.classList.remove('s-done', 's-over', 's-soon', 's-new', 's-progress');
      card.classList.add(d.cardState);
    }

    // Timing line — expanded shows "<b>phrase</b> · date", compact just the phrase.
    if (d.timingPhrase !== undefined) {
      const overdue = d.timingOverdue === 'true';
      const expanded = card.querySelector('.jlv-timing');
      if (expanded) {
        expanded.classList.toggle('jlv-timing--over', overdue);
        expanded.textContent = '';
        const b = document.createElement('b');
        b.textContent = d.timingPhrase;
        expanded.appendChild(b);
        if (d.timingDate) expanded.appendChild(document.createTextNode(` · ${d.timingDate}`));
      }
      const compact = card.querySelector('.tlv-rtiming');
      if (compact) {
        compact.classList.toggle('tlv-rtiming--over', overdue);
        compact.textContent = d.timingPhrase;
      }
    }
  }

  // Message for a form fetch that did not return a popup fragment: the
  // server's plain-text reason for a refused request, otherwise a generic
  // nudge (a redirect means the page or session moved on).
  async blockedMessage(response) {
    const contentType = response.headers.get('content-type') || '';
    if (!response.redirected && !response.ok && contentType.includes('text/plain')) {
      const text = (await response.text()).trim();
      if (text) return text;
    }
    return 'This editor could not be opened. Reload the page and try again.';
  }

  // Text-only popup in place of the editor. Escape / outside click close it.
  showNotice(trigger, message) {
    this.popupElement.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'p-3 text-xs text-gray-700';
    box.style.width = '260px';
    box.dataset.popupRole = 'notice';
    box.textContent = message;
    this.popupElement.appendChild(box);
    this.popupElement.classList.remove('hidden');
    this.position(trigger);
  }

  clearErrors() {
    const existing = this.popupElement.querySelector('[data-popup-role="errors"]');
    if (existing) existing.remove();
  }

  showErrors(messages) {
    this.clearErrors();
    const errEl = document.createElement('div');
    errEl.dataset.popupRole = 'errors';
    errEl.className = 'mt-2 p-2 text-xs rounded';
    errEl.style.cssText = 'background:#fee2e2;color:#991b1b;border:1px solid #fecaca;';
    errEl.innerHTML = messages.map((m) => `<div>${this.escapeHtml(m)}</div>`).join('');
    // Insert before the Cancel/Save buttons row if we can find it.
    const buttons = this.popupElement.querySelector('[data-popup-action="save"]');
    const buttonsRow = buttons ? buttons.parentElement : null;
    if (buttonsRow) {
      buttonsRow.parentNode.insertBefore(errEl, buttonsRow);
    } else {
      this.popupElement.appendChild(errEl);
    }
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  handleKey(event) {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      this.save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  handleGlobalKey(event) {
    if (event.key === 'Escape' && this.popupElement && !this.popupElement.classList.contains('hidden')) {
      this.close();
    }
  }

  // Remember where a press started: picking a TomSelect option re-renders
  // (or closes) its dropdown on mousedown, so by the time the click fires
  // the target can be a detached node or the page behind the popup, which
  // would read as an outside click and throw the edit away.
  handleMousedown(event) {
    this.pressStartedInside = !!(this.popupElement && this.popupElement.contains(event.target));
  }

  handleOutsideClick(event) {
    if (!this.popupElement || this.popupElement.classList.contains('hidden')) return;
    if (this.popupElement.contains(event.target)) return;
    if (this.currentTrigger && this.currentTrigger.contains(event.target)) return;
    if (this.pressStartedInside) { this.pressStartedInside = false; return; }
    this.close();
  }

  position(trigger) {
    const rect = trigger.getBoundingClientRect();
    const popupRect = this.popupElement.getBoundingClientRect();
    const popupWidth = popupRect.width || 260;
    const popupHeight = popupRect.height || 200;
    const margin = 8;

    let left = rect.left + window.scrollX;
    if (left + popupWidth + margin > window.innerWidth + window.scrollX) {
      left = Math.max(margin, window.innerWidth + window.scrollX - popupWidth - margin);
    }

    // Flip above the trigger if the bottom-anchored popup would go off-screen.
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let top;
    if (spaceBelow >= popupHeight + 4 || spaceBelow >= spaceAbove) {
      top = rect.bottom + window.scrollY + 4;
    } else {
      top = rect.top + window.scrollY - popupHeight - 4;
      if (top < window.scrollY + margin) top = window.scrollY + margin;
    }

    this.popupElement.style.left = `${left}px`;
    this.popupElement.style.top = `${top}px`;
  }

  showSpinner(on) {
    const spinner = this.popupElement.querySelector('[data-popup-role="spinner"]');
    const text = this.popupElement.querySelector('[data-popup-role="save-text"]');
    const btn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (spinner) spinner.classList.toggle('hidden', !on);
    if (text) text.textContent = on ? 'Saving…' : 'Save';
    if (btn) btn.disabled = on;
  }
}
